// React
import { useContext, useState, useEffect, useRef, useCallback } from "react";
// Hooks
import useMaxLengthInput from "../../hooks/useMaxLengthInput";
import { BiShow, BiHide } from "react-icons/bi";
import { useNavigate, useLocation } from "react-router-dom";
// Service
import { POST, GET } from "../../service/CRUDService";
import { GLogin, microsoftAuth } from "../../service/OAuthLogin";
import {
  ONBOARDING_PATH,
  shouldStartOnboarding,
} from "../../routes/onboardingAccess";
import { logError } from "../../telemetry";
import {
  fetchRecaptchaRequirement,
  isValidEmailFormat,
  normalizeEmail,
} from "../../service/userPermissions";
// Utils
import { schema } from "./SignInSideCurtains.utils";
// Third Party
import { Form, Formik } from "formik";
import { useGoogleLogin } from "@react-oauth/google";
import ReCAPTCHA from "react-google-recaptcha";
// Constants
import EntityConstants from "../../configs/EntityConstants";
import AppConstants from "../../configs/AppConstants";
// Store & Context
import AuthContext from "../../store/auth-context";
// Components
import Toast from "../../common/Toast/Toast";
// Icons
import GoogleIcon from "../../resources/icons/Google-Social-icon.svg";
import MicrosoftIcon from "../../resources/icons/microsoft.svg";
import mailIcon from "../../resources/icons/icon-mail.svg";
import lockIcon from "../../resources/icons/icon-lock.svg";
import eyeOffIcon from "../../resources/icons/icon-eye-off.svg";
import { useTranslation } from "react-i18next";
import { automationTestIds } from "../../testing/automationTestIds";

const RECAPTCHA_CHECK_DEBOUNCE_MS = 400;
const REMEMBER_ME_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const FormValueSync = ({ values, setCachedValues }) => {
  useEffect(() => {
    setCachedValues(values);
  }, [values, setCachedValues]);
  return null;
};

const SignInSideCurtains = ({
  variant = "modal",
  toggleComponent,
  closeModal,
  onAccountNotActive,
  cachedValues,
  setCachedValues,
  clearCache,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const authContext = useContext(AuthContext);
  const { t } = useTranslation();
  const recaptchaRef = useRef(null);
  const captchaCheckAbortRef = useRef(null);
  const captchaCheckDebounceRef = useRef(null);
  const checkedEmailRef = useRef(null);
  const requestedEmailRef = useRef(null);
  const initialCachedEmailRef = useRef(cachedValues?.email);

  const [hideShowpassword, setHideShowpassword] = useState("password");
  const [captchaValue, setCaptchaValue] = useState(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [isCaptchaRequired, setIsCaptchaRequired] = useState(true);
  const { limitReached, handleMaxLengthKeyDown, clearLimitReached } =
    useMaxLengthInput();

  //Local CSS vars:
  const InputBorderRadius = "0.5rem";
  const CheckboxFocusRadius = "0.3rem";
  const isLandingVariant = variant === "landing";

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
  };

  const resetCaptchaWidget = useCallback(() => {
    setCaptchaValue(null);
    recaptchaRef.current?.reset();
  }, []);

  const checkRecaptchaForEmail = useCallback(
    async (email) => {
      const trimmed = email?.trim();
      if (!isValidEmailFormat(trimmed)) {
        checkedEmailRef.current = null;
        requestedEmailRef.current = null;
        setIsCaptchaRequired(true);
        return;
      }

      const normalized = normalizeEmail(trimmed);
      if (checkedEmailRef.current === normalized) {
        return;
      }

      captchaCheckAbortRef.current?.abort();
      const controller = new AbortController();
      captchaCheckAbortRef.current = controller;
      requestedEmailRef.current = normalized;

      try {
        const required = await fetchRecaptchaRequirement(trimmed, {
          signal: controller.signal,
        });
        if (
          controller.signal.aborted ||
          requestedEmailRef.current !== normalized
        ) {
          return;
        }
        checkedEmailRef.current = normalized;
        setIsCaptchaRequired(required);
        if (!required) {
          resetCaptchaWidget();
        }
      } catch (error) {
        if (error?.name === "AbortError") {
          return;
        }
        if (requestedEmailRef.current !== normalized) {
          return;
        }
        setIsCaptchaRequired(true);
      }
    },
    [resetCaptchaWidget],
  );

  const scheduleRecaptchaCheck = useCallback(
    (email) => {
      clearTimeout(captchaCheckDebounceRef.current);
      captchaCheckDebounceRef.current = setTimeout(() => {
        checkRecaptchaForEmail(email);
      }, RECAPTCHA_CHECK_DEBOUNCE_MS);
    },
    [checkRecaptchaForEmail],
  );

  const handleEmailIdentityChange = useCallback(
    (nextEmail) => {
      const normalized = normalizeEmail(nextEmail);

      if (!isValidEmailFormat(nextEmail)) {
        clearTimeout(captchaCheckDebounceRef.current);
        captchaCheckAbortRef.current?.abort();
        requestedEmailRef.current = null;
        if (checkedEmailRef.current) {
          checkedEmailRef.current = null;
          resetCaptchaWidget();
        }
        setIsCaptchaRequired(true);
        return;
      }

      if (normalized === checkedEmailRef.current) {
        return;
      }

      clearTimeout(captchaCheckDebounceRef.current);
      captchaCheckAbortRef.current?.abort();
      requestedEmailRef.current = null;

      const hadResolvedEmail = checkedEmailRef.current !== null;
      checkedEmailRef.current = null;
      setIsCaptchaRequired(true);

      if (hadResolvedEmail) {
        resetCaptchaWidget();
      }

      scheduleRecaptchaCheck(nextEmail);
    },
    [resetCaptchaWidget, scheduleRecaptchaCheck],
  );

  const toggleForgotPasswordModal = () => {
    authContext.forgotPasswordHandler();
    authContext.setTriggerLoginModuleToggleValue(false);
  };

  const clearStoredTokens = () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("userType");
  };

  const handleSubmit = async (values) => {
    const cleanedValues = {
      ...values,
      email: values.email.trim(),
    };
    try {
      if (isCaptchaRequired && !captchaValue) {
        Toast({
          type: "error",
          title: t("authPages:captcha_error"),
          message: t("authPages:captcha_incomplete"),
        });
        return;
      }

      // Remember email and password if the checkbox is checked
      if (rememberMe) {
        const timestamp = new Date().getTime(); // Current time in milliseconds
        localStorage.setItem("remembered_email", cleanedValues.email);
        localStorage.setItem("remembered_timestamp", timestamp.toString());
      } else {
        localStorage.removeItem("remembered_email");
        localStorage.removeItem("remembered_timestamp");
      }

      const requestBody = isCaptchaRequired
        ? { ...cleanedValues, captchaToken: captchaValue }
        : cleanedValues;

      const response = await POST(EntityConstants.LOGIN, requestBody);

      if (isCaptchaRequired) {
        resetCaptchaWidget();
      }

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem("auth_token", data.access);
        localStorage.setItem("refresh_token", data.refresh);

        clearCache();

        const userResponse = await GET(EntityConstants.CURRENT_USER);
        if (!userResponse.ok) {
          clearStoredTokens();
          throw new Error(t("authPages:failed_fetch_user_data"));
        }

        const userData = await userResponse.json();
        const permissions = await authContext.bootstrapUserSession(userData);
        if (!permissions) {
          Toast({
            type: "error",
            title: t("authPages:login_failed"),
            message: t("authPages:failed_fetch_user_data"),
          });
          return;
        }

        Toast({
          type: "success",
          title: t("authPages:success"),
          message: t("authPages:logged_in_successfully"),
        });

        if (userData?.is_first_time_login === true) {
          localStorage.removeItem("redirectAfterLogin");
          closeModal();
          navigate("/users/reset-password");
          return;
        }

        if (
          shouldStartOnboarding(
            permissions,
            userData?.type || userData?.user_type,
          )
        ) {
          localStorage.removeItem("redirectAfterLogin");
          closeModal();
          navigate(ONBOARDING_PATH);
          return;
        }

        const redirectUrl = localStorage.getItem("redirectAfterLogin");
        if (redirectUrl) {
          localStorage.removeItem("redirectAfterLogin");
          navigate(redirectUrl);
        } else if (location.pathname === "/") {
          navigate("/");
        }
        closeModal();
      } else {
        let errorData;
        try {
          errorData = await response.json();
        } catch (jsonError) {
          errorData = {};
        }

        if (errorData.error === "account_not_active") {
          if (onAccountNotActive) {
            onAccountNotActive(errorData.message || "");
          } else {
            Toast({
              type: "error",
              title: t("authPages:login_failed"),
              message: errorData.message || t("authPages:inactive_account"),
            });
          }
          return;
        }

        if (response.status === 401) {
          const isInactiveUser =
            errorData.error === "User is not active." ||
            (errorData.message?.includes("not active") && !errorData.error);

          if (isInactiveUser) {
            Toast({
              type: "error",
              title: t("authPages:login_failed"),
              message: t("authPages:inactive_account"),
            });
          } else {
            Toast({
              type: "error",
              title: t("authPages:login_failed"),
              message:
                errorData.message ||
                errorData.error ||
                t("authPages:invalid_credentials"),
            });
          }
        } else {
          Toast({
            type: "error",
            title: t("authPages:error"),
            message:
              errorData.message ||
              errorData.error ||
              t("authPages:login_error"),
          });
        }
      }
    } catch (error) {
      if (isCaptchaRequired) {
        resetCaptchaWidget();
      }
      clearStoredTokens();
      console.error("Login failed:", error);
      const httpError = error || {};
      logError(error instanceof Error ? error.message : "Login failed", {
        event: "login_failure",
        ...(httpError.httpStatus
          ? {
              "http.status_code": httpError.httpStatus,
              "http.method": httpError.httpMethod ?? "POST",
              endpoint: httpError.httpUrl ?? EntityConstants.LOGIN,
            }
          : {}),
      });
      Toast({
        type: "error",
        title: t("authPages:login_failed"),
        message: error?.message || t("authPages:login_error"),
      });
    }
  };

  useEffect(() => {
    const storedEmail = localStorage.getItem("remembered_email");
    const storedTimestamp = localStorage.getItem("remembered_timestamp");
    let emailToCheck = initialCachedEmailRef.current?.trim();

    if (storedEmail && storedTimestamp) {
      const currentTime = new Date().getTime();
      const timeDifference = currentTime - parseInt(storedTimestamp, 10);

      if (timeDifference <= REMEMBER_ME_TTL_MS) {
        setCachedValues((prev) => ({
          ...prev,
          email: storedEmail,
        }));
        setRememberMe(true);
        emailToCheck = storedEmail;
      } else {
        localStorage.removeItem("remembered_email");
        localStorage.removeItem("remembered_timestamp");
      }
    }

    if (isValidEmailFormat(emailToCheck)) {
      checkRecaptchaForEmail(emailToCheck);
    }

    return () => {
      captchaCheckAbortRef.current?.abort();
      clearTimeout(captchaCheckDebounceRef.current);
      requestedEmailRef.current = null;
    };
  }, [checkRecaptchaForEmail, setCachedValues]);

  const login = useGoogleLogin({
    onSuccess: async (credentialResponse) => {
      const loginSuccess = await GLogin(credentialResponse, "success");
      if (loginSuccess === true) {
        try {
          // Fetch user data after successful Google login
          const userResponse = await GET(EntityConstants.CURRENT_USER);
          if (!userResponse.ok) {
            throw new Error(t("authPages:failed_fetch_user_data"));
          }
          const userData = await userResponse.json();

          const permissions = await authContext.bootstrapUserSession(userData);
          if (!permissions) {
            Toast({
              type: "error",
              title: t("authPages:login_failed"),
              message: t("authPages:failed_fetch_user_data"),
            });
            return;
          }

          authContext.googlelogin();

          // Close modal and redirect if needed
          closeModal();

          if (
            shouldStartOnboarding(
              permissions,
              userData?.type || userData?.user_type,
            )
          ) {
            localStorage.removeItem("redirectAfterLogin");
            navigate(ONBOARDING_PATH);
            return;
          }

          const redirectUrl = localStorage.getItem("redirectAfterLogin");
          if (redirectUrl) {
            localStorage.removeItem("redirectAfterLogin");
            navigate(redirectUrl);
          } else {
            navigate("/");
          }
        } catch (error) {
          Toast({
            type: "error",
            title: t("authPages:login_failed"),
            message: t("authPages:failed_fetch_user_data"),
          });
        }
      }
    },
    onError: (error) => {
      GLogin(error, "failed");
      Toast({
        type: "error",
        title: t("authPages:google_login_failed"),
        message: t("authPages:authentication_error"),
      });
    },
  });

  return (
    <>
      <Formik
        // enableReinitialize
        initialValues={cachedValues || { email: "", password: "" }}
        validationSchema={schema(t)}
        validateOnMount
        onSubmit={(values) => handleSubmit(values)}
      >
        {({
          values,
          touched,
          errors,
          handleChange,
          handleBlur,
          handleSubmit,
          isValid,
          isSubmitting,
        }) => (
          <Form
            noValidate
            onSubmit={handleSubmit}
            id={automationTestIds.signinForm}
          >
            <FormValueSync values={values} setCachedValues={setCachedValues} />
            {isLandingVariant ? (
              <>
                <div className="mb-8 flex flex-col gap-1.5">
                  <h1 className="text-[30px] font-extrabold leading-tight text-cfSignInSignUpHeadingText">
                    {t("authPages:welcome_back")}
                  </h1>
                  <p className="text-sm leading-5 text-cfSignInSignUpIntroMessageText">
                    {t("authPages:sign_in_description")}
                  </p>
                </div>

                <div className="flex w-full flex-col gap-[18px]">
                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor={automationTestIds.signinEmailInput}
                      className="text-[13px] font-semibold text-cfSignInSignUpInputLabelText"
                    >
                      {t("authPages:email")}{" "}
                      <span className="text-cfDashboardChartRevoked">*</span>
                    </label>
                    <div className="flex h-[46px] items-center gap-2.5 rounded-[8px] border border-cfSignInSignUpInputBorder px-3.5">
                      <img
                        src={mailIcon}
                        alt=""
                        className="size-[18px] shrink-0"
                        aria-hidden="true"
                      />
                      <input
                        type="text"
                        name="email"
                        id={automationTestIds.signinEmailInput}
                        required
                        placeholder={t("authPages:enter_email")}
                        autoComplete="email"
                        className="min-w-0 flex-1 border-none bg-transparent text-sm text-cfSignInSignUpHeadingText placeholder:text-cfSignInSignUpInputPlaceholderText focus:outline-none"
                        maxLength={100}
                        value={values.email}
                        onChange={(e) => {
                          handleChange(e);
                          if (e.target.value.length < 100)
                            clearLimitReached("email");
                          handleEmailIdentityChange(e.target.value);
                        }}
                        onKeyDown={handleMaxLengthKeyDown}
                        onBlur={(e) => {
                          handleBlur(e);
                          clearTimeout(captchaCheckDebounceRef.current);
                          if (isValidEmailFormat(e.target.value)) {
                            checkRecaptchaForEmail(e.target.value);
                          } else {
                            checkedEmailRef.current = null;
                            setIsCaptchaRequired(true);
                          }
                        }}
                      />
                    </div>
                    <p className="text-sm text-red-600">
                      {limitReached.email
                        ? t("authPages:max_100_characters")
                        : errors.email && touched.email && `${errors.email}`}
                    </p>
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label
                      htmlFor={automationTestIds.signinPasswordInput}
                      className="text-[13px] font-semibold text-cfSignInSignUpInputLabelText"
                    >
                      {t("authPages:password")}{" "}
                      <span className="text-cfDashboardChartRevoked">*</span>
                    </label>
                    <div className="flex h-[46px] items-center gap-2.5 rounded-[8px] border border-cfSignInSignUpInputBorder px-3.5">
                      <img
                        src={lockIcon}
                        alt=""
                        className="size-[18px] shrink-0"
                        aria-hidden="true"
                      />
                      <input
                        type={hideShowpassword}
                        name="password"
                        id={automationTestIds.signinPasswordInput}
                        required
                        placeholder={t("authPages:password")}
                        autoComplete="current-password"
                        className="min-w-0 flex-1 border-none bg-transparent text-sm text-cfSignInSignUpHeadingText placeholder:text-cfSignInSignUpInputPlaceholderText focus:outline-none"
                        maxLength={100}
                        value={values.password}
                        onChange={(e) => {
                          handleChange(e);
                          if (e.target.value.length < 100)
                            clearLimitReached("password");
                        }}
                        onKeyDown={handleMaxLengthKeyDown}
                        onBlur={handleBlur}
                      />
                      <button
                        type="button"
                        id={
                          hideShowpassword === "password"
                            ? automationTestIds.signinPasswordShow
                            : automationTestIds.signinPasswordHide
                        }
                        className="bg-transparent p-0 text-cfSignInSignUpInputPlaceholderText"
                        aria-label={
                          hideShowpassword === "password"
                            ? t("authPages:show_password")
                            : t("authPages:hide_password")
                        }
                        onClick={() =>
                          setHideShowpassword((current) =>
                            current === "password" ? "text" : "password",
                          )
                        }
                      >
                        {hideShowpassword === "password" ? (
                          <img
                            src={eyeOffIcon}
                            alt=""
                            className="size-[18px]"
                            aria-hidden="true"
                          />
                        ) : (
                          <BiShow className="size-[18px]" />
                        )}
                      </button>
                    </div>
                    <p className="text-sm text-red-600">
                      {limitReached.password
                        ? t("authPages:max_100_characters")
                        : errors.password &&
                          touched.password &&
                          `${errors.password}`}
                    </p>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        name="remember"
                        id={automationTestIds.signinRememberMe}
                        className="size-[18px] rounded border border-cfSignInSignUpInputBorder focus:outline-none"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                      />
                      <label
                        htmlFor={automationTestIds.signinRememberMe}
                        className="text-[13px] font-medium text-cfSignInSignUpInputPlaceholderText"
                      >
                        {t("authPages:remember_30_days")}
                      </label>
                    </div>
                    <button
                      type="button"
                      id={automationTestIds.signinForgotPassword}
                      className="text-[13px] font-semibold text-cfForgotPasswordText underline"
                      onClick={toggleForgotPasswordModal}
                    >
                      {t("authPages:forgot_password")}
                    </button>
                  </div>
                </div>

                <div className="mt-6 flex w-full flex-col gap-4">
                  <button
                    type="submit"
                    id={automationTestIds.signinSubmit}
                    disabled={
                      !isValid ||
                      isSubmitting ||
                      (isCaptchaRequired && !captchaValue)
                    }
                    aria-busy={isSubmitting}
                    className={`inline-flex h-12 w-full items-center justify-center gap-2 rounded-[8px] text-[15px] font-bold text-cfSecondary transition-colors duration-200 ${
                      isSubmitting
                        ? "cursor-wait bg-cfButton"
                        : isValid && (!isCaptchaRequired || captchaValue)
                          ? "cursor-pointer bg-cfButton hover:bg-cfPrimaryDark"
                          : "cursor-not-allowed bg-cfDisabled"
                    }`}
                  >
                    {isSubmitting && (
                      <svg
                        className="size-4 shrink-0 animate-spin text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                    )}
                    {isSubmitting
                      ? t("authPages:signing_in")
                      : t("authPages:sign_in")}
                  </button>

                  <div
                    className={`w-full overflow-hidden transition-all duration-200 ease-in-out ${
                      isCaptchaRequired
                        ? "max-h-[78px] opacity-100"
                        : "max-h-0 opacity-0"
                    }`}
                    aria-hidden={!isCaptchaRequired}
                  >
                    {isCaptchaRequired && (
                      <div className="custom-recaptcha-width flex items-center justify-center">
                        <ReCAPTCHA
                          ref={recaptchaRef}
                          sitekey={AppConstants.GOOGLE_CAPTCHA_SITE_KEY}
                          onChange={handleCaptchaChange}
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-3 text-xs font-semibold text-cfSignInSignUpOrText">
                    <div className="h-px flex-1 bg-cfCardBorder" />
                    <span>{t("authPages:or")}</span>
                    <div className="h-px flex-1 bg-cfCardBorder" />
                  </div>

                  <div className="flex gap-2">
                    <button
                      type="button"
                      id={automationTestIds.signinGoogle}
                      onClick={login}
                      className="flex h-11 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-[8px] border border-cfSignInSignUpInputBorder bg-cfSecondary px-2"
                    >
                      <img
                        src={GoogleIcon}
                        alt=""
                        className="size-4 shrink-0"
                      />
                      <span className="whitespace-nowrap text-[11px] font-semibold leading-none text-cfSignInSignUpHeadingText sm:text-xs">
                        {t("authPages:sign_in_with_google")}
                      </span>
                    </button>
                    <button
                      type="button"
                      id={automationTestIds.signinMicrosoft}
                      onClick={microsoftAuth}
                      className="flex h-11 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-[8px] border border-cfSignInSignUpInputBorder bg-cfSecondary px-2"
                    >
                      <img
                        src={MicrosoftIcon}
                        alt=""
                        className="size-4 shrink-0"
                      />
                      <span className="whitespace-nowrap text-[11px] font-semibold leading-none text-cfSignInSignUpHeadingText sm:text-xs">
                        {t("authPages:sign_in_with_microsoft")}
                      </span>
                    </button>
                  </div>
                </div>

                <div className="mt-8 flex items-center justify-center gap-1 text-sm text-cfDontHaveAccountText">
                  <p>{t("authPages:no_account")}</p>
                  <button
                    type="button"
                    id={automationTestIds.signinSwitchToSignup}
                    onClick={toggleComponent}
                    className="font-bold text-cfDontHaveAccountSignUpText underline focus:outline-none"
                  >
                    {t("authPages:sign_up")}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="text-4xl font-medium leading-6 text-cfSignInSignUpHeadingText px-8">
                  {t("authPages:sign_in")}
                </h3>
                <div className="flex flex-col justify-center items-center text-left w-full h-full overflow-hidden font-CfPrimaryFont px-8 py-6">
                  <div className="py-3 text-start w-full">
                    <p className="text-cfSignInSignUpIntroMessageText text-base">
                      {t("authPages:sign_in_description")}
                    </p>
                  </div>
                  <div
                    id="inputSignUp"
                    className={`flex flex-col gap-[0.625rem] w-full`}
                  >
                    <div className="flex flex-col gap-2 rounded-md bg-inherit w-full">
                      <label
                        htmlFor={automationTestIds.signinEmailInput}
                        className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText after:content-['*'] after:text-red-500"
                      >
                        {t("authPages:email")}&nbsp;
                      </label>

                      <input
                        type="text"
                        name="email"
                        id={automationTestIds.signinEmailInput}
                        required
                        placeholder={t("authPages:enter_email")}
                        autoComplete="email"
                        className="rounded-md w-full h-12 focus:outline-none border border-cfSignInSignUpInputBorder text-base text-cfSignInSignUpInputPlaceholderText"
                        style={{ borderRadius: InputBorderRadius }}
                        maxLength={100}
                        value={values.email}
                        onChange={(e) => {
                          handleChange(e);
                          if (e.target.value.length < 100)
                            clearLimitReached("email");
                          handleEmailIdentityChange(e.target.value);
                        }}
                        onKeyDown={handleMaxLengthKeyDown}
                        onBlur={(e) => {
                          handleBlur(e);
                          clearTimeout(captchaCheckDebounceRef.current);
                          if (isValidEmailFormat(e.target.value)) {
                            checkRecaptchaForEmail(e.target.value);
                          } else {
                            checkedEmailRef.current = null;
                            setIsCaptchaRequired(true);
                          }
                        }}
                      />
                    </div>
                    <p className="text-red-600">
                      {limitReached.email
                        ? t("authPages:max_100_characters")
                        : errors.email && touched.email && `${errors.email}`}
                    </p>
                    <div className="flex flex-col gap-2 rounded-md bg-inherit w-full">
                      <label
                        htmlFor={automationTestIds.signinPasswordInput}
                        className="my-auto font-medium text-sm text-cfSignInSignUpInputLabelText after:content-['*'] after:text-red-500"
                      >
                        {t("authPages:password")}&nbsp;
                      </label>
                      <div className="flex items-center justify-between rounded-md w-full h-12 mr-[.14rem] pl-2 border border-cfSignInSignUpInputBorder">
                        <input
                          type={hideShowpassword}
                          name="password"
                          id={automationTestIds.signinPasswordInput}
                          required
                          placeholder={t("authPages:password")}
                          autoComplete="current-password"
                          className="focus:outline-none text-base text-cfSignInSignUpInputPlaceholderText border-none w-full"
                          value={values.password}
                          style={{ borderRadius: InputBorderRadius }}
                          maxLength={100}
                          onChange={(e) => {
                            handleChange(e);
                            if (e.target.value.length < 100)
                              clearLimitReached("password");
                          }}
                          onKeyDown={handleMaxLengthKeyDown}
                          onBlur={handleBlur}
                        />
                        <div className="pl-1 pr-2 text-cfSignInSignUpInputPlaceholderText">
                          {hideShowpassword === "password" && (
                            <button
                              type="button"
                              id={automationTestIds.signinPasswordShow}
                              className="cursor-pointer bg-transparent border-none p-0"
                              aria-label={t("authPages:password")}
                              onClick={() => setHideShowpassword("text")}
                            >
                              <BiShow className="cursor-pointer" />
                            </button>
                          )}
                          {hideShowpassword === "text" && (
                            <button
                              type="button"
                              id={automationTestIds.signinPasswordHide}
                              className="cursor-pointer bg-transparent border-none p-0"
                              aria-label={t("authPages:password")}
                              onClick={() => setHideShowpassword("password")}
                            >
                              <BiHide className="cursor-pointer" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    <p className="text-red-600">
                      {limitReached.password
                        ? t("authPages:max_100_characters")
                        : errors.password &&
                          touched.password &&
                          `${errors.password}`}
                    </p>
                  </div>
                  <div className="flex text-base justify-between items-center w-full mt-3">
                    <div>
                      <input
                        type="checkbox"
                        name="remember"
                        id={automationTestIds.signinRememberMe}
                        className="focus:ring-transparent focus:outline-none peer mx-1 mb-0.5 border border-cfSignInSignUpInputBorder"
                        style={{ borderRadius: CheckboxFocusRadius }}
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                      />
                      <label
                        htmlFor={automationTestIds.signinRememberMe}
                        className={`mb-0 ml-1 text-sm font-medium`}
                      >
                        {t("authPages:remember_30_days")}
                      </label>
                    </div>
                    <div>
                      <a
                        href="#forgot_password"
                        id={automationTestIds.signinForgotPassword}
                        className={`text-cfForgotPasswordText hover:text-cfPrimaryDark text-sm font-medium focus:outline-none`}
                        onClick={(e) => {
                          e.preventDefault();
                          toggleForgotPasswordModal();
                        }}
                      >
                        {t("authPages:forgot_password")}
                      </a>
                    </div>
                  </div>
                  <div className="w-full mt-6 flex flex-col gap-4">
                    <div
                      className={`w-full flex justify-center overflow-hidden transition-all duration-200 ease-in-out ${
                        isCaptchaRequired
                          ? "max-h-[78px] opacity-100"
                          : "max-h-0 opacity-0"
                      }`}
                      aria-hidden={!isCaptchaRequired}
                    >
                      {isCaptchaRequired && (
                        <div className="flex custom-recaptcha-width justify-center items-center">
                          <ReCAPTCHA
                            ref={recaptchaRef}
                            sitekey={AppConstants.GOOGLE_CAPTCHA_SITE_KEY}
                            onChange={handleCaptchaChange}
                          />
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-2 items-center">
                      <button
                        type="submit"
                        id={automationTestIds.signinSubmit}
                        disabled={
                          !isValid ||
                          isSubmitting ||
                          (isCaptchaRequired && !captchaValue)
                        }
                        aria-busy={isSubmitting}
                        className={`rounded-xl text-base font-semibold py-1 px-5 text-cfSecondary w-full h-11 transition-colors duration-200 inline-flex items-center justify-center gap-2 ${
                          isSubmitting
                            ? "bg-cfButton cursor-wait"
                            : isValid && (!isCaptchaRequired || captchaValue)
                              ? "bg-cfButton hover:bg-cfPrimaryDark cursor-pointer"
                              : "bg-cfDisabled cursor-not-allowed"
                        }`}
                      >
                        {isSubmitting && (
                          <svg
                            className="animate-spin h-4 w-4 shrink-0 text-cfSecondary"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            />
                          </svg>
                        )}
                        {isSubmitting
                          ? t("authPages:signing_in")
                          : t("authPages:sign_in")}
                      </button>
                    </div>
                    <div className="flex items-center w-full gap-2.5 text-cfSignInSignUpOrText">
                      <hr className="w-1/2" />
                      <p>{t("authPages:or")}</p>
                      <hr className="w-1/2" />
                    </div>
                    <div className="flex gap-4">
                      <button
                        type="button"
                        id={automationTestIds.signinGoogle}
                        onClick={login}
                        className="flex items-center gap-2 justify-center w-full h-11 border rounded-full"
                      >
                        <img src={GoogleIcon} alt="Google" />
                        <p className="font-semibold text-base font-CfPrimaryFont ">
                          {t("authPages:sign_in")}
                        </p>
                      </button>
                      <button
                        type="button"
                        id={automationTestIds.signinMicrosoft}
                        onClick={microsoftAuth}
                        className="flex items-center gap-2 justify-center w-full h-11 border rounded-full"
                      >
                        <img src={MicrosoftIcon} alt="Microsoft" />
                        <p className="font-semibold text-base font-CfPrimaryFont ">
                          {t("authPages:sign_in")}
                        </p>
                      </button>
                    </div>
                  </div>
                  <div className="flex text-[1rem] leading-[1.2rem] px-0.5 pb-2 text-sm mt-5 mb-3">
                    <p className="text-cfDontHaveAccountText">
                      {t("authPages:no_account")}
                    </p>
                    &nbsp;
                    <button
                      type="button"
                      id={automationTestIds.signinSwitchToSignup}
                      onClick={toggleComponent}
                      className="flex focus:outline-none"
                    >
                      <p className="hover:text-cfPrimaryDark font-semibold text-cfDontHaveAccountSignUpText">
                        {t("authPages:sign_up")}
                      </p>
                    </button>
                  </div>
                </div>
              </>
            )}
          </Form>
        )}
      </Formik>
    </>
  );
};

export default SignInSideCurtains;
